import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Coin from "@/components/game/Coin";
import { bankCardSrc, CARD_RATIO } from "@/lib/artAssets";

// CLIMATE BANK (v3 step 5): a TAB in the event-decks row, right of the Global deck, showing the
// shared balance (and "Vote open"). Click it: a centred pop-up with the balance, "Pay in", the 3
// projects and "Propose" (the hand stays visible in the right column). A proposal opens a vote that
// every other seat answers in the selected-card area (ResponseFlow, kind "bank_vote"), so the
// pop-up closes on Propose.
//   game.climate_bank  public state (balance, payments, projects, open vote)
//   game.bank_view     live costs / squares / why the active player can't propose (server)
//   enabled            the free part of the active player's turn on this screen
export const BANK_PROJECTS = [
  { key: "green_roofs", name: "Green Roofs for All", blurb: "A Green Roof on every building" },
  { key: "living_coastline", name: "Living Coastline", blurb: "A Mangrove on every empty coastal square" },
  { key: "clean_energy", name: "Clean Energy Transition", blurb: "House floors emit no carbon" },
];

export function costText(key, bv) {
  if (!bv?.costs) return "";
  const cost = bv.costs[key];
  if (key === "living_coastline") {
    const n = bv.squares?.length ?? 0;
    return n ? `${cost} (${bv.per_square} × ${n} square${n === 1 ? "" : "s"})` : "no empty coastal square";
  }
  return `${cost} (${bv.per_player?.[key]} × ${bv.players} players)`;
}

export default function ClimateBankPanel({ game, player, enabled = false, busy = false, onPay, onPropose, focusDim = false }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(1);
  const bank = game.climate_bank || {};
  const bv = game.bank_view || {};
  const money = player?.money ?? 0;
  const debt = player?.debt ?? 0;
  const vote = bank.vote || null;
  const voteBy = vote ? game.players.find((p) => p.color === vote.by)?.name : null;
  const balance = bank.balance ?? 0;
  const nameOf = (c) => game.players.find((p) => p.color === c)?.name || c;

  useEffect(() => {
    setAmount((a) => Math.max(1, Math.min(a, Math.max(1, money))));
  }, [money]);

  // a response window (bank vote, Protection, Board Thief) is answered in the selected-card area: don't cover it
  const responding = !!game.response_state;
  useEffect(() => {
    if (responding) setOpen(false);
  }, [responding]);

  const payReason = !enabled
    ? "Pay in on your own turn"
    : debt > 0
      ? "Pay off your debt first"
      : money < 1
        ? "You have no coins to pay in"
        : null;
  const last = bank.last;
  const lastText = vote
    ? `Vote open: ${voteBy} proposed ${BANK_PROJECTS.find((p) => p.key === vote.key)?.name}`
    : last?.kind === "pay"
      ? `${nameOf(last.color)} paid in ${last.amount}`
      : last?.kind === "vote"
        ? `${BANK_PROJECTS.find((p) => p.key === last.key)?.name} ${last.result === "agreed" ? "agreed" : "not agreed"}`
        : null;

  return (
    <>
      <button type="button" data-testid="bank-tab" data-vote={vote ? "true" : "false"} onClick={() => setOpen(true)} aria-haspopup="dialog" aria-label={`Climate Bank, balance ${balance}${vote ? ", vote open" : ""}. Open`}
        title={lastText || "Climate Bank: pay coins in or propose a project"}
        className="shrink-0 flex flex-col items-center justify-center self-stretch list-row-lift" style={{ width: 104, padding: "4px 6px", borderRadius: 10, background: "var(--surface)", border: vote ? "2px solid var(--sea)" : "1px solid var(--line)", boxShadow: vote ? "0 0 0 3px rgba(31,111,139,.18)" : "none", opacity: focusDim ? 0.5 : 1, lineHeight: "11px" }}>
        <span style={{ fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink-4)" }}>Climate Bank</span>
        <span className="inline-flex items-center gap-1" style={{ marginTop: 5, fontWeight: 700, fontSize: 18, lineHeight: "22px", color: "var(--ink)" }}>
          <Coin size={18} />
          <span data-testid="bank-balance" className="tabular-nums">{balance}</span>
        </span>
        <span style={{ marginTop: 4, fontSize: 10.5, lineHeight: "13px", fontWeight: vote ? 700 : 400, color: vote ? "var(--sea)" : "var(--ink-4)" }}>{vote ? "Vote open" : "Click to open"}</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="bank-dialog" className="max-w-lg max-h-[calc(90*var(--svh))] overflow-y-auto" style={{ background: "var(--surface)", border: "1px solid var(--line)", boxShadow: "var(--shadow-card)" }}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Climate Bank</DialogTitle>
            <DialogDescription className="sr-only">Shared pot of coins: pay in or propose a project.</DialogDescription>
          </DialogHeader>
          <div className="inline-flex items-center gap-2" style={{ fontWeight: 700, fontSize: 30, lineHeight: "34px", color: "var(--ink)" }}>
            <Coin size={30} />
            <span data-testid="bank-balance-big" className="tabular-nums">{balance}</span>
          </div>
          {lastText && <div data-testid="bank-last" style={{ fontSize: 13, color: vote ? "var(--sea)" : "var(--ink-4)", fontWeight: vote ? 700 : 400 }}>{lastText}</div>}
          <div data-testid="bank-body" className="flex flex-col" style={{ gap: 14 }}>
  <div className="flex items-center" style={{ gap: 8 }}>
            <span style={{ fontSize: 13, color: "var(--ink-3)" }}>Everyone shares it. No refunds.</span>
            <span className="ml-auto inline-flex items-center" style={{ gap: 4 }}>
              <button type="button" data-testid="bank-pay-minus" aria-label="Pay in less" disabled={!!payReason || amount <= 1} onClick={() => setAmount((a) => Math.max(1, a - 1))} className="btn-secondary btn-compact" style={{ width: 30, height: 32, padding: 0 }}><Minus size={12} /></button>
              <input data-testid="bank-pay-amount" type="number" inputMode="numeric" min={1} max={Math.max(1, money)} value={amount} aria-label="Coins to pay in" disabled={!!payReason}
                onChange={(e) => setAmount(Math.max(1, Math.min(Math.max(1, money), Math.floor(Number(e.target.value)) || 1)))}
                className="tabular-nums text-center" style={{ width: 46, height: 32, borderRadius: 6, border: "1px solid var(--line)", background: "var(--surface)", fontSize: 13, fontWeight: 700 }} />
              <button type="button" data-testid="bank-pay-plus" aria-label="Pay in more" disabled={!!payReason || amount >= money} onClick={() => setAmount((a) => Math.min(money, a + 1))} className="btn-secondary btn-compact" style={{ width: 30, height: 32, padding: 0 }}><Plus size={12} /></button>
              <button type="button" data-testid="bank-pay-btn" disabled={!!payReason || busy} title={payReason || `Pay ${amount} into the Climate Bank`} onClick={() => onPay(amount)} className="btn-primary btn-compact" style={{ height: 32, padding: "0 14px", whiteSpace: "nowrap" }}>
                Pay in {amount}
              </button>
            </span>
          </div>
          <ul className="flex flex-col" style={{ gap: 10 }}>
            {BANK_PROJECTS.map((p) => {
              const st = bank.projects?.[p.key] || {};
              const cost = bv.costs?.[p.key];
              const short = cost != null ? cost - balance : 0;
              const reason = st.done ? "Done: each project can happen once per game" : bv.reasons?.[p.key] || null;
              const blocked = !enabled || !!reason || busy;
              const art = bankCardSrc(p.key);
              return (
                <li key={p.key} data-testid={`bank-project-${p.key}`} data-done={st.done ? "true" : "false"} className="flex items-center min-w-0" style={{ gap: 12, opacity: st.done ? 0.65 : 1 }}>
                  {art && <img src={art} alt="" width={72} height={Math.round(72 * CARD_RATIO)} className="shrink-0" style={{ width: 72, height: Math.round(72 * CARD_RATIO), borderRadius: 5, objectFit: "cover", border: "1px solid var(--line)" }} />}
                  <span className="min-w-0 flex-1" title={p.blurb}>
                    <span className="block" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", lineHeight: "18px" }}>{p.name}</span>
                    <span className="block" style={{ fontSize: 11.5, color: "var(--ink-4)", lineHeight: "15px" }}>{p.blurb}</span>
                    <span data-testid={`bank-cost-${p.key}`} className="block" style={{ fontSize: 12.5, color: "var(--ink-3)", lineHeight: "16px" }}>
                      {st.done ? `Done in round ${st.round} · ${nameOf(st.by)} proposed` : costText(p.key, bv) + (short > 0 && cost ? ` · ${short} short` : "")}
                    </span>
                  </span>
                  <button type="button" data-testid={`bank-propose-${p.key}`} disabled={blocked} title={reason || (!enabled ? "Propose on your own turn" : `Ask everyone to agree to ${p.name}`)} onClick={() => { setOpen(false); onPropose(p.key); }} className="btn-secondary btn-compact shrink-0" style={{ height: 32, padding: "0 14px" }}>
                    {st.done ? "Done" : "Propose"}
                  </button>
                </li>
              );
            })}
          </ul>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
